import {escapes} from "./expr/Expr.js";

export function checked_cast<T>(type: { new(...args): T }, value: any): T {
    if (value instanceof type) return value; else throw "Expected value of type "+type;
}

export function promisify<T>(functionToExec: Function, thisObject: any = null): ((...args: any[]) => Promise<T>) {
	return function() {
		var array = Object.values(arguments);
		return new Promise((resolve, reject) => {
			array.push(resolve)
			try {
				functionToExec.apply(thisObject, array);
			} catch (error) {
				reject(error);
			}
		});
	}
}
export class RetryError extends Error {}
export async function retry<T>(times: number, delay: number, action: () => (Promise<T> | T), exceptionType?: Function, cancel?: AbortSignal): Promise<T> {
    while(times--) {
        try {
            cancel?.throwIfAborted();
            return await action();
        } catch(e) {
            if (!times || (exceptionType && !(e instanceof exceptionType))) throw e;
            cancel?.throwIfAborted();
            await new Promise((r,_) => setTimeout(r, delay));
        }
    }
    throw "this will never happen";
}
export function clamp(val: number, min: number, max: number) {
    return Math.min(Math.max(val, min), max);
}
export function typedArrayToBuffer(array: Uint8Array): SharedArrayBuffer | ArrayBuffer {
	return array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset)
}
export function bufferToHex (buffer : Uint8Array | ArrayBuffer, delim : string = "", limit?: number) {
    return Array
		.from (new Uint8Array(limit ? buffer.slice(0, Math.min(limit, buffer.byteLength)) : buffer))
		.map (b => b.toString (16).padStart (2, "0"))
		.join (delim);
}
export function hexToBuffer (str: string): Uint8Array {
    const uint8array = new Uint8Array(Math.ceil(str.length / 2));
    for (let i = 0; i < str.length;)
        uint8array[i / 2] = Number.parseInt(str.slice(i, i += 2), 16);
    return uint8array;
}
export function getDateTime(){
    return new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
}
export function getTimeString(){
    return new Date().toISOString().substring(11,19);
}
export function splitInTwoParts(str: string, delim: string): [string, string | undefined] {
    const index = str.indexOf(delim);
    if (index === -1) return [str, undefined];
    else return [str.substring(0, index), str.substring(index + 1)];
}

/**
 * restricts str to a maximum length of maxLength.
 * if str needs to be cut off, the contents of format are appended (defaults to "...").
 * @param str string to ellipsize
 * @param maxLength if str is longer than, maxLength, it will be cut off. the contents of format will be appended, so the result may be longer than maxLength
 * @param format may contain `%d`, which will be replaced by the number of chars omitted
 */
export function ellipsize(str: string, maxLength: number, format: string = '...'): string {
    str = `${str}`;
    return str.substring(0, maxLength) + (str.length > maxLength ? format.replace('%d', `${str.length-maxLength}`) : '');
}
function regexOrString(s): string | RegExp {
    if (/^[a-zA-Z0-9_-]+$/.test(s)) return s; else return new RegExp("^"+s+"$");
}
function filterUndefined<T>(array: (T | undefined)[]): T[] {
    return array.filter(x => x !== undefined) as T[];
}

function mapFirst<T,U>(array: T[], fn: (arg0: T) => U): U | undefined {
    for (const el of array) {
        const result = fn(el);
        if (result) return result;
    }
    return undefined;
}

/**
 * tokenize search string
 */
const escapesWithSpace = Object.assign({' ': ' '}, escapes);
export function splitEscaped(str: string): string[] {
    if (!str) return [];
    const o : string[] = [];
    let escaped = false, quoted = false, x = 0;
    for(let i = 0; i < str.length; i++) {
        if (escaped) {
            const char = escapesWithSpace[str[i]];
            if (!char) throw new Error("splitEscaped: invalid escape \\"+str[i]);
            o[x] = (o[x] || '') + char; escaped = false;
        } else if (str[i] === '"') {
            quoted = !quoted; o[x] = (o[x] || '');
        } else if (str[i] === '\\') {
            escaped = true;
        } else if (quoted) {
            o[x] += str[i];
        } else if (str[i] === ' ' || str[i] === '\t' || str[i] === '\n' || str[i] === '\r') {
            if (o[x] !== undefined) ++x;
        } else {
            o[x] = (o[x] || '') + str[i];
        }
    }
    return o;
}

export function split2(str: string, sep: string): [string, string|undefined] {
    const idx = str.indexOf(sep);
    if (idx === -1) return [str, undefined];
    return [str.substring(0, idx), str.substring(idx + 1)];
}

export function splitEscapedKv(str: string): Record<string, string> {
    return attrListToKv(splitEscaped(str));
}

export function attrListToKv(attrs: string[]): Record<string, string> {
    const o: Record<string, string> = {};
    for(const s in attrs) {
        const [k, v] = split2(s, '=');
        o[k] = v === undefined ? '' : v;
    }
    return o;
}

/**
 * Matches objects against a search string.
 *
 * Search string syntax:
 *  - separated on whitespace into tokens (escape whitespace using backslash, quote using double-quote `"`)
 *  - all tokens need to match (implicit conjunction)
 *  - token may start with ! (exclamation point) to invert (object only matches if rest of token doesn't match)
 *  - token may start with : (colon) to add matching key to `secondaryKeys` (usage depending on user of the class, they may e.g. be shown as columns in the result grid)
 *  - token may start with key= (for any key consisting of letters, numbers, - and _) to only match rest of token in this key
 *  - token may start with keyRegex= (for any valid regex (without slashes)) to only match rest of token in keys matching the keyRegex
 *  - token may start with / (slash) to match as regular expression
 *  - token may start with = (equals sign), will be search case-sensitively (anywhere in string)
 *  - token may be any other string, will be searched case-insensitively (anywhere in string)
 *
 * By default, items where key `deleted` exists and has a true-ish value, are ignored. If
 * a token starting with deleted= is provided, this implicit rule is removed.
 *
 * Examples:
 *
 *     icon=checked.svg hafas_legs=
 *       item where the string contents of `icon` contain `checked.svg` (ignoring case) and
 *       where a key `hafas_legs` exists (content irrelevant)
 *
 *     click.*Method= deleted=1
 *       item which has a key matching the regex /^click.*Method$/, show only deleted items
 *
 */
export class SearchMatcher {
    hideDeleted = true;
    secondaryKeys : (RegExp|string)[] = [];
    searchStrings : [boolean, string|RegExp|null, boolean, string|RegExp|number][];
    defaultIgnoreKeys = new Set();

    isDefaultIgnored(key: string): boolean {
        return this.defaultIgnoreKeys.has(key);
    }

    transformToken(s: string): string {
        return s;
    }

    splitEscaped(str: string): string[] {
        return splitEscaped(str);
    }

    /**
     * @param checkFor search string. syntax described in class doc
     */
    constructor(checkFor:string) {
        this.searchStrings = filterUndefined(this.splitEscaped(checkFor).map((s): [boolean, string|RegExp|null, boolean, string|RegExp|number] | undefined => {
            var m;
            var invert = false;
            var display = false;
            if (s.startsWith("!")) { invert=true; s = s.substring(1); }
            if (s.startsWith(':')) { display=true; s = s.substring(1); }
            s = this.transformToken(s);
            if (s === undefined) return undefined;
            if (m = s.match(/([^\\=][^=]*)=(.*)/)) {
                var key = regexOrString(m[1]);
                if (key === 'deleted') this.hideDeleted=false;
                if (display) this.secondaryKeys.push(key);
                return [invert,key, ...this.parseRegex(m[2])];
            } else if (display) {
                this.secondaryKeys.push(regexOrString(s)); return undefined;
            } else return [invert,null, ...this.parseRegex(s)];
        }));
    }
    parseRegex(str): [boolean, string|RegExp|number] {
        var m;
        if (m = str.match(/^\/(.*)\/([a-z]*)$/)) return [false,new RegExp(m[1], m[2])]; 
        else if (str.startsWith('/')) return [false,new RegExp(str.substring(1), 'i')];
        else if (str.startsWith('=')) return [false,str.substring(1)];
        else if (m = str.match(/^([<>])(?:(\d+)|([@%a-zA-Z].*))$/)) return [m[1] === '>', m[2] ? parseInt(m[2]) : +Date.parseRelDatetime(m[3])];
        else return [true,str.toLowerCase()];
    }

    /**
     * check whether `item` matches the search string provided in the constructor
     * @param item object with string keys and values of any type
     * @returns boolean
     */
    isMatch(item: {[key: string]: any}): boolean {
        if (!item || typeof item !== 'object') return false;
        if (item.deleted && this.hideDeleted) return false;

        var itemEntries = Object.entries(item);
        return this.searchStrings.every(([invert,kr,ignorecase,vr]) => {
            const vmatch = vr instanceof RegExp
                 ? (v) => vr.test(`${v}`)
                 : typeof vr === 'number'
                 ? (ignorecase /*gt*/
                    ? (v) => +v > vr : (v) => +v < vr
                 ) : ignorecase
                    ? (v) => `${v}`.toLowerCase().indexOf(vr) !== -1
                    : (v) => `${v}` == vr;
            return invert != (
                typeof kr === 'string'
                ? (kr in item && vmatch(item[kr]))
                : itemEntries.some(([k,v]) => (kr == null ? !this.isDefaultIgnored(k) : kr.test(k)) && vmatch(v))
            );
        });
    }

    /**
     * check whether `item` matches the search string provided in the constructor (same as isMatch),
     * but if it matches, returns an array of ExplainResult, showing the match positions.
     * returns false otherwise.
     */
    explainMatch(item: {[key: string]: any}): ExplainResult[] | false {
        if (!item.deleted || !this.hideDeleted) {
            var itemEntries = Object.entries(item);
            const explain : ExplainResult[] = [];
            for (const [invert,kr,ignorecase,vr] of this.searchStrings) {
                const valuematch = vr instanceof RegExp 
                    ? (k: string, v: any): ExplainResult | false => { const res = vr.exec(`${v}`); return res ? [k, res.index, res[0].length] : false; }
                    : typeof vr === 'number'
                    ? (ignorecase /*gt*/
                        ? (k: string, v: any): ExplainResult | false => (+v > vr) && [k, 0, `${v}`.length] : (k: string, v: any): ExplainResult | false => (+v < vr) && [k, 0, `${v}`.length]
                    ) : ignorecase
                        ? (k: string, v: any): ExplainResult | false => { const res = `${v}`.toLowerCase().indexOf(vr); return res !== -1 ? [k, res, vr.length] : false; }
                        : (k: string, v: any): ExplainResult | false => { const res = `${v}` == vr; return res ? [k, 0, vr.length] : false; }
                const match = typeof kr === 'string' 
                    ? (kr in item && valuematch(kr, item[kr]))
                    : mapFirst(itemEntries, ([k,v]) => (kr == null ? !this.isDefaultIgnored(k) : kr.test(k)) && valuematch(k, v));
                if (invert != !match) return false;
                if (match) explain.push(match);
            }
            return explain;
        }
        return false;
    }
}
type ExplainResult = [string, number, number];

export function dateToLocalISOString(date: Date) {
	return (new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()).slice(0, -1);
}
export function dateFromLocalISOString(dateTimeLocalValue: string) {
	const fakeUtcTime = new Date(`${dateTimeLocalValue}Z`);
	return new Date(fakeUtcTime.getTime() + fakeUtcTime.getTimezoneOffset() * 60000);
}

export function deepEqual(obj1: any, obj2: any): boolean {

    if(obj1 === obj2) // it's just the same object. No need to compare.
        return true;

    if(isPrimitive(obj1) || isPrimitive(obj2)) // compare primitives
        return obj1 === obj2;

    if(Object.keys(obj1).length !== Object.keys(obj2).length)
        return false;

    if (obj1?.constructor !== obj2?.constructor)
        return false;

    // compare objects with same number of keys
    for(let key in obj1)
    {
        if(!(key in obj2)) return false; //other object doesn't have this prop
        if(!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
}

//check if value is primitive
function isPrimitive(obj)
{
    return (obj !== Object(obj));
}
export function compareObjects(obj1: object, obj2: object): {op:string,k:string|null}[] {
	var result : {op:string,k:string|null}[] = [];
    if (obj1 === obj2) return [];
    if (isPrimitive(obj1) || isPrimitive(obj2)) return (obj1 === obj2) ? [] : [{op:'c', k:null}];
	for(const [k,v] of Object.entries(obj1)) {
		if (!(k in obj2)) result.push({op:'d', k: k});
		else if (!deepEqual(obj2[k], v)) result.push({op:'c', k: k});
	}
	for(const k in obj2) {
		if (!(k in obj1)) result.push({op:'a', k: k});
	}
	return result;
}
export function compareObjectsVerbose(obj1: object, obj2: object): {op:string,k:string|null,old?:any,new?:any}[] {
	var result : {op:string,k:string|null,old?:any,new?:any}[] = [];
    if (obj1 === obj2) return [];
    if (isPrimitive(obj1) || isPrimitive(obj2)) return (obj1 === obj2) ? [] : [{op:'c', k:null, old:obj1, new:obj2}];
	for(const [k,v] of Object.entries(obj1)) {
		if (!(k in obj2)) result.push({op:'d', k: k, old: v});
		else if (!deepEqual(obj2[k], v)) result.push({op:'c', k: k, old: v, new: obj2[k]});
	}
	for(const k in obj2) {
		if (!(k in obj1)) result.push({op:'a', k: k, new: obj2[k]});
	}
	return result;
}
export function shallowClone<T>(o: T): T
export function shallowClone<T, U>(o: T, p: U): T & U
export function shallowClone<T, U, V>(o: T, p: U, q: V): T & U & V
export function shallowClone(...o: object[]): object {
    return Object.assign({}, ...o);
}

export function filterObject<O extends object>(o: O, filterFn: (val: [string, any], index: number, array: [string, any][]) => boolean): O {
	return Object.fromEntries(Object.entries(o).filter(filterFn)) as O;
}
interface Ctx<T, A extends any[], R> extends Omit<ClassMethodDecoratorContext<T, Fn<T, A, R>>, 'private'> {
    private: false;
}
type Fn<T, A extends any[], R> = (this: T, ...args: A) => R;
type Decorator<T, A extends any[], R> = (target: any, ctx: Ctx<T, A, R>) => void;

function nameFn<T extends {name: string}>(value: string, fn: T): T {
    Object.defineProperty(fn, 'name', {
        configurable: true,
        value,
        writable: true
    });

    return fn;
}

export function BoundMethod<T, A extends any[], R>(): Decorator<T, A, R>;
export function BoundMethod<T, A extends any[], R>(...args: Partial<A>): Decorator<T, A, R>;
export function BoundMethod<T, A extends any[], R>(...args: Partial<A>): Decorator<T, A, R> {
    return function boundMethodDecorator(origFn: Function, {addInitializer, name}) {
        const sName = String(name);
        const boundName = `Bound(${sName})`;

        const performBind: (inst: T) => Fn<T, A, R> = nameFn(
            `BoundMethodBinder(${sName})`,
            args.length
                ? (inst => nameFn(boundName, origFn.bind(inst, ...args)))
                : (inst => nameFn(boundName, origFn.bind(inst)))
        );

        addInitializer(nameFn(`BoundMethodInitialiser(${sName})`, function (this: T) {
            this[name as keyof T] = performBind(this) as any;
        }));
    };
}


export function cache<C,R>(value: any, ctx: ClassGetterDecoratorContext<C, R>) {
    if (ctx.kind === "getter") {
        return function () {
            const result = value.call(this);
            Object.defineProperty(
                this, ctx.name,
                {
                    value: result,
                    writable: false,
                }
            );
            return result;
        };
    }
}

/**
 * returns the UNIX timestamp in milliseconds
 */
export function now(): number {
    return +new Date();
}

export function secondsElapsedSince(seconds: number, timestamp: number): boolean {
    return (Date.now() - timestamp) / 1000 > seconds;
}

export function sum(iter): number {
    let s = 0;
    for(const x of iter) s += +x;
    return s;
}

declare global {
    interface DateConstructor {
        parseInterval(interval: TimeInterval): number;
        parseDocDate(documentId?: string): DateRange & {user:string} | null;
        fromISO8601Week(year: number, week: number): Date;
        parseRelDatetime(input: string | number, documentDateRange?: DateRange, base?: Date): Date;
        formatInterval(interval: number): string;
    }
    interface Date {
        /**
         * return a new Date which is later by the given interval
         * @param interval time interval in parseInterval format
         */
        plus (interval: TimeInterval): Date;

        /**
         * return a new Date which is earlier by the given interval
         * @param interval time interval in parseInterval format
         */
        minus (interval: TimeInterval): Date;

        /**
         * get the year and week number according to the ISO week date system
         * (as described in ISO 8601 https://en.wikipedia.org/wiki/ISO_week_date)
         */
        iso8601Week(): [number, number];

        relDatetime(input: TimeInterval): Date;
        isToday(): boolean;
        isSameDate(otherDate: Date): boolean;
    }
}

export type TimeInterval = string | number;
export type DateRange = { start: Date, end: Date };
const intervalUnits = { 'ms': 1, 's': 1000, 'm': 60000, 'h': 3600000, 'd': 86400000, 'w': 86400000*7 };
const weekdays = ['mo','di','mi','do','fr','sa','so'];
Date.parseDocDate = function(documentId?: string): DateRange & {user:string} | null {
    if (!documentId) return null;
    let m;
    if (m = /^([a-z]+)\/journal\/(\d{4})-(\d{2})-(\d{2})$/.exec(documentId)) {
        var datum = new Date(m[2]-0, m[3]-1, m[4]-0);
        return {user: m[1], start: datum, end: datum.plus(86400000)};
    }
    if (m = /^([a-z]+)\/journal\/(\d{4})-W(\d{2})$/.exec(documentId)) {
        var datum = Date.fromISO8601Week(m[2]-0, m[3]-0);
        return {user: m[1], start: datum, end: datum.plus(86400000*7)};
    }
    return null;
};
Date.parseInterval = function(interval: TimeInterval): number {
    if (typeof interval === 'number') {
        return interval;
    } else if (typeof interval === 'string') {
        let minus = 1;
        if (interval.startsWith('-')) { minus = -1; interval = interval.substring(1); }
        else if (interval.startsWith('+')) { interval = interval.substring(1); }
        const matches = interval.split(/([0-9]+)([a-z]+)/g);
        if (!matches) throw "invalid interval format "+interval;
        let sum = 0;
        if (matches[matches.length-1]) throw "invalid interval format "+interval+" (unexpected "+matches[matches.length-1]+")";
        for(let i = 1; i+2 < matches.length; i += 3) {
            if (!(matches[i+1] in intervalUnits)) throw "invalid interval unit "+matches[i+1]+" in "+interval;
            sum += +matches[i] * intervalUnits[matches[i+1]];
            if (matches[i+2]) throw "invalid interval format "+interval+" (unexpected "+matches[i]+")";
        }
        return minus * sum;
    } else {
        throw "invalid interval type "+typeof interval;
    }
}
Date.formatInterval = function(interval: number): string {
    const units = Object.entries(intervalUnits);
    let out = "";
    if (interval < 0) { out = "-"; interval = -interval; }
    for(let i = units.length-1; i >= 0; i--) {
        const unitAmount = Math.floor(interval / units[i][1]);
        if (unitAmount > 0) {
            out += `${unitAmount}${units[i][0]}`;
            interval -= unitAmount * units[i][1];
        }
    }
    return out;
}
Date.prototype.plus = function(interval: TimeInterval): Date {
    return new Date(+this + Date.parseInterval(interval));
}
Date.prototype.minus = function(interval: TimeInterval): Date {
    return new Date(+this - Date.parseInterval(interval));
}
Date.prototype.relDatetime = function(input: TimeInterval): Date {
    if (typeof input === 'number') return this.plus(input);
    let now = this;
    for(const clause of input.split(/\s+/g)) {
        let m, i;
        if (clause.startsWith('+') || clause.startsWith('-'))
            now = now.plus(clause);
        
        else if ((m = clause.match(/^([a-zA-Z]{2})$/)) && (i = weekdays.indexOf(m[1].toLowerCase())) !== -1) {
            now = new Date(+now + (- (now.getDay() || 7) + i + 1) * 86400000);
        }
        else if (m = clause.match(/^([0-9]{1,2}):([0-9]{2})(?::([0-9]{2}))?$/)) {
            now = new Date(+now);
            now.setHours(+m[1], +m[2], +(m[3] || 0), 0);
        }
        else if (m = clause.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/)) {
            now = new Date(+m[1], +m[2]-1, +m[3], now.getHours(), now.getMinutes(), now.getSeconds());
        }
        else if (clause === '') now = now;
        else throw "invalid relDate clause "+clause;
    }
    return now;
}
Date.prototype.iso8601Week = function () {
    // Create a copy of the current date, we don't want to mutate the original
    const date = new Date(this.getTime());
  
    // Find Thursday of this week starting on Monday
    date.setDate(date.getDate() + 4 - (date.getDay() || 7));
    const thursday = date.getTime();
  
    // Find January 1st
    date.setMonth(0); // January
    date.setDate(1);  // 1st
    const jan1st = date.getTime();
  
    // Round the amount of days to compensate for daylight saving time
    const days = Math.round((thursday - jan1st) / 86400000); // 1 day = 86400000 ms
    return [date.getFullYear(), Math.floor(days / 7) + 1];
};
Date.prototype.isSameDate = function(otherDate) {
    return (otherDate.getFullYear() == this.getFullYear() && otherDate.getMonth() == this.getMonth() && otherDate.getDate() == this.getDate());
}
Date.prototype.isToday = function() {
    return this.isSameDate(new Date());
}
Date.fromISO8601Week = function(year: number, week: number): Date {
    const date = new Date(year, 0, 1);
    if (date.getDay() !== 4)
        date.setMonth(0, 1 + ((4 - date.getDay()) + 7) % 7);
    date.setDate(date.getDate() + (week - 1) * 7 - 3);
    return date;
}
Date.parseRelDatetime = function(input: string | number, documentDateRange?: DateRange, base?: Date) {
    if (!base) base = new Date();
    if (typeof input === 'number') return base.plus(input);
    if ((input.startsWith('%') && (input = input.substring(1), true))) base = new Date();
    else if (documentDateRange && ((input.startsWith('@') && (input = input.substring(1), true)) || !(input.startsWith('+') || input.startsWith('-') || input === ''))) base = documentDateRange.start;
    
    return base.relDatetime(input);
}


declare global {
    interface Array<T> {
        mapDefined<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[];
    }
}
Object.defineProperty(Array.prototype, 'mapDefined', {
    enumerable: false,
    value: function<T, U>(this: Array<T>, callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[] {
        return this.map(callbackfn, thisArg).filter((x: U) => x !== undefined);
    }
});


export class CancelledError extends Error { constructor() { super("Action cancelled by user."); } }

export function isEqualBytesConstTime(
    bytes1: Uint8Array,
    bytes2: Uint8Array
  
): boolean {

    if (bytes1.length !== bytes2.length) {
        return false;
    }
    let equal = true;
    for (let i = 0; i < bytes1.length; i++) {
        if (bytes1[i] !== bytes2[i]) {
            equal = false;
        }
    }

    return equal;

}

export type CsvParserOptions = {
    fSep?: string,
    rSep?: string,
    quot?: string,
    head?: boolean,
    trim?: boolean
};
export function csvToArray(str: string, options: CsvParserOptions): string[][] | {[o: string]: string}[] {
    const o = Object.assign({
        'fSep': ',',
        'rSep': '\n',
        'quot': '"',
        'head': false,
        'trim': false
    }, options);
    var a = [
        ['']
    ];
    for (var r = 0, f = 0, p = 0, q = 0; p < str.length; p++) {
        var c;
      
        switch (c = str.charAt(p)) {
        case o.quot:
            if (q && str.charAt(p + 1) == o.quot) {
                a[r][f] += o.quot;
                ++p;
            } else {
                q ^= 1;
            }
            break;
        case o.fSep:
            if (!q) {
                if (o.trim) {
                    a[r][f] = a[r][f].replace(/^\s\s*/, '').replace(/\s\s*$/, '');
                }
                a[r][++f] = '';
            } else {
                a[r][f] += c;
            }
            break;
        case o.rSep.charAt(0):
            if (!q && (!o.rSep.charAt(1) || (o.rSep.charAt(1) && o.rSep.charAt(1) == str.charAt(p + 1)))) {
                if (o.trim) {
                    a[r][f] = a[r][f].replace(/^\s\s*/, '').replace(/\s\s*$/, '');
                }
                a[++r] = [''];
                a[r][f = 0] = '';
                if (o.rSep.charAt(1)) {
                    ++p;
                }
            } else {
                a[r][f] += c;
            }
            break;
        default:
            a[r][f] += c;
        }
    }
    if (a[a.length - 1].length < a[0].length) {
        a.pop()
    }
    if (o.head) {
        const head = a.shift();
        if (!head) return [];
        return a.map(row => Object.fromEntries(row.map((f, i) => ([head[i], f]))));
    }
    return a;
}


/*
XXX not sure whether this works properly (separate for each instance)

export function debounce<P extends unknown[], This>(timeMs: number) : (fn_: (...args: P) => void, ctx: ClassMethodDecoratorContext<This>) => void {
    return function(fn_: (...args: P) => void, ctx: ClassMethodDecoratorContext<This>): (...args: P) => void {
        let timer = null;
        return (...args: P): void => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            timer = setTimeout(() => fn_(...args), timeMs);
        };
    };
}
*/

export function debounced<P extends unknown[]>(timeMs: number, fn_: (...args: P) => void, reset = true): (...args: P) => void {
    let timer : any = null;
    let args_ : any = null;
    return (...args: P): void => {
        //console.log("debounced fn called")
        if (timer && reset)
            clearTimeout(timer);
        args_ = args;
        if (reset || !timer)
            timer = setTimeout(() => { timer = null; fn_(...args_); }, timeMs);
    };
}

export function splitItemRef(ref: string): [string, string] {
    const parts = ref.split('#');
    if (parts.length === 2) return parts as [string, string];
    else if (parts.length === 1) {
        const slash = ref.lastIndexOf('/');
        if (slash < 1) throw new Error("Invalid ItemRef "+ref);
        return [ref.substring(0, slash), ref.substring(slash + 1)];
    } else throw new Error("Invalid ItemRef "+ref);
}